import Axios from "axios";
import { msalInstance } from "../msalInstance";
import { profileRequest, protectedResources } from "../authConfig";
import { shareInFlight } from "../helpers/shareInFlight";

const GRAPH_ME_EMPLOYEE_ID = `${protectedResources.graphMe.endpoint}?$select=employeeId`;
// After a failed lookup (no consent, network), wait before asking Graph again instead of retrying on every API call.
const RETRY_AFTER_FAILURE_MS = 5 * 60 * 1000;

const inFlight = new Map();
const failedAt = new Map();

const storageKey = (email) => `person_id_${(email || '').toLowerCase()}`;

// Entra ID employeeId of the signed-in user. Simond calls it "personId" and looks it up in under a second,
// much faster than by email. Resolves to undefined when there is none, so callers fall back to email.
// PageLayout starts this right after sign-in, so API calls usually find the answer already stored.
export function getPersonId(email = '') {
  const key = storageKey(email);
  const stored = sessionStorage.getItem(key);
  // "" means Entra has no employeeId for this user: remembered so Graph is not asked again.
  if (stored !== null) return Promise.resolve(stored || undefined);
  if (!inFlight.has(key) && Date.now() - (failedAt.get(key) || 0) < RETRY_AFTER_FAILURE_MS) return Promise.resolve(undefined);

  return shareInFlight(inFlight, key, () => lookupPersonId(email, key));
}

async function lookupPersonId(email, key) {
  try {
    const accounts = msalInstance.getAllAccounts();
    const account =
      accounts.find(a => (a.username || '').toLowerCase() === (email || '').toLowerCase()) ||
      msalInstance.getActiveAccount() ||
      accounts[0];
    if (!account) return undefined;

    // Same scopes as PageLayout's Graph token, so it is served from MSAL's cache.
    const { accessToken } = await msalInstance.acquireTokenSilent({ ...profileRequest, account });
    const response = await Axios.get(GRAPH_ME_EMPLOYEE_ID, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const personId = response?.data?.employeeId || '';
    sessionStorage.setItem(key, personId);
    return personId || undefined;
  } catch (error) {
    failedAt.set(key, Date.now());
    console.warn('Cannot read employeeId from Microsoft Graph, using email instead', error?.message);
    return undefined;
  }
}
